import React, { useState, useEffect, useCallback } from "react";
import {
  MapPin,
  Navigation,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Shield,
  Layers,
  ChevronDown,
  Info,
  Sliders,
  Clock,
  ExternalLink,
} from "lucide-react";
import MapPreview from "../../components/geofence/MapPreview";
import MapLocationSearchBar from "../../components/geofence/MapLocationSearchBar";
import GeoFenceStatus from "../../components/geofence/GeoFenceStatus";
import EditSiteGeoFenceModal from "../../components/geofence/EditSiteGeoFenceModal";
import {
  getCurrentGPSLocation,
  fetchSiteGeoFence,
  saveSiteGeoFence,
  fetchGeoFenceOverview,
  GEOFENCE_STATUS,
} from "../../services/geofenceService";
import {
  Allprojects,
  getProjectsByOrganization,
  getProjectsByOwnership,
  getProjectsByOrgOwnership,
  getProjectsForCurrentUser,
  getProjectUserDetails,
} from "../../api";
import { resolveSafetyOrganizationId } from "../../utils/safetyProfileHelpers";
import { showToast } from "../../utils/toast";

/**
 * Resolves the authoritative active organization ID from storage,
 * user profile, active accesses, and decoded JWT token.
 */
export const resolveCurrentOrganizationId = () => {
  try {
    // 1. Direct explicit storage keys
    const directKeys = ["ACTIVE_ORG_ID", "ORG_ID", "org_id", "organization_id"];
    for (const key of directKeys) {
      const val = localStorage.getItem(key);
      if (val && !isNaN(Number(val)) && Number(val) > 0) {
        return Number(val);
      }
    }

    // 2. USER_DATA storage
    const rawUserData = localStorage.getItem("USER_DATA");
    if (rawUserData && rawUserData !== "undefined" && rawUserData !== "null") {
      const userData = JSON.parse(rawUserData);
      const userOrg =
        userData.org ??
        userData.org_id ??
        userData.organization_id ??
        userData.organization?.id ??
        userData.organization;
      if (userOrg && !isNaN(Number(userOrg)) && Number(userOrg) > 0) {
        return Number(userOrg);
      }

      // Check accesses inside USER_DATA
      if (Array.isArray(userData.accesses) && userData.accesses.length > 0) {
        for (const acc of userData.accesses) {
          const accOrg =
            acc.organization_id ??
            acc.org_id ??
            acc.org ??
            acc.project?.organization_id ??
            acc.project?.organization;
          if (accOrg && !isNaN(Number(accOrg)) && Number(accOrg) > 0) {
            return Number(accOrg);
          }
        }
      }
    }

    // 3. JWT Token Payload
    const token =
      localStorage.getItem("ACCESS_TOKEN") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("access") ||
      localStorage.getItem("token") ||
      localStorage.getItem("TOKEN");
    if (token) {
      const cleanToken = String(token).replace(/^Bearer\s+/i, "");
      const parts = cleanToken.split(".");
      if (parts.length === 3) {
        const payloadStr = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
        const payload = JSON.parse(payloadStr);
        const tokenOrg =
          payload.org ??
          payload.org_id ??
          payload.organization_id ??
          payload.organization?.id ??
          payload.organization;
        if (tokenOrg && !isNaN(Number(tokenOrg)) && Number(tokenOrg) > 0) {
          return Number(tokenOrg);
        }
        if (Array.isArray(payload.accesses) && payload.accesses.length > 0) {
          for (const acc of payload.accesses) {
            const accOrg =
              acc.organization_id ??
              acc.org_id ??
              acc.org ??
              acc.project?.organization_id;
            if (accOrg && !isNaN(Number(accOrg)) && Number(accOrg) > 0) {
              return Number(accOrg);
            }
          }
        }
      }
    }

    // 4. Safety profile helper fallback
    const safetyOrg = resolveSafetyOrganizationId();
    if (safetyOrg && !isNaN(Number(safetyOrg)) && Number(safetyOrg) > 0) {
      return Number(safetyOrg);
    }
  } catch (err) {
    console.warn("Could not resolve organization ID:", err);
  }
  return null;
};

/**
 * Dynamically resolves the organization name if available.
 */
export const resolveCurrentOrganizationName = () => {
  try {
    const rawUserData = localStorage.getItem("USER_DATA");
    if (rawUserData && rawUserData !== "undefined" && rawUserData !== "null") {
      const userData = JSON.parse(rawUserData);
      const name =
        userData.organization_name ??
        userData.org_name ??
        userData.organization?.name ??
        userData.organization?.organization_name;
      if (name && typeof name === "string" && name.trim()) {
        return name.trim();
      }
    }
    const storedName =
      localStorage.getItem("ORGANIZATION_NAME") ||
      localStorage.getItem("org_name") ||
      localStorage.getItem("active_org_name");
    if (storedName && typeof storedName === "string" && storedName.trim()) {
      return storedName.trim();
    }
  } catch {}
  return "";
};

export default function SiteGeoFencing() {
  // State
  const [projects, setProjects] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [activeOrgName, setActiveOrgName] = useState("");
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [selectedSite, setSelectedSite] = useState(null);

  // Form State
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState(100);
  const [enabled, setEnabled] = useState(true);

  // GPS Capture State
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [capturedLocation, setCapturedLocation] = useState(null);
  const [isCapturingGPS, setIsCapturingGPS] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Overview Table State
  const [overviewList, setOverviewList] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSite, setLoadingSite] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  // Edit Geofence Modal State
  const [editingSite, setEditingSite] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Load projects list and overview dynamically based on the logged-in admin's assigned organization
  const loadProjectsAndOverview = useCallback(async () => {
    setLoadingOverview(true);
    try {
      let currentOrgId = resolveCurrentOrganizationId();
      let currentOrgName = resolveCurrentOrganizationName();

      // 1. Fetch current admin's assigned projects directly from /user-stage-role/get-projects-by-user/
      // Each admin has specific projects assigned to their user account (e.g. Safety.Admin -> Project A)
      let assignedProjects = [];
      try {
        const userProjectsRes = await getProjectUserDetails();
        if (Array.isArray(userProjectsRes?.data)) {
          assignedProjects = userProjectsRes.data;
        } else if (Array.isArray(userProjectsRes?.data?.projects)) {
          assignedProjects = userProjectsRes.data.projects;
        } else if (Array.isArray(userProjectsRes?.data?.results)) {
          assignedProjects = userProjectsRes.data.results;
        }
      } catch (err) {
        console.warn("getProjectUserDetails fallback:", err);
      }

      // Fallback: If no assigned projects found via user-stage-role, try getProjectsForCurrentUser
      if (assignedProjects.length === 0) {
        try {
          const curRes = await getProjectsForCurrentUser();
          if (Array.isArray(curRes?.data)) {
            assignedProjects = curRes.data;
          } else if (Array.isArray(curRes?.data?.projects)) {
            assignedProjects = curRes.data.projects;
          } else if (Array.isArray(curRes?.data?.results)) {
            assignedProjects = curRes.data.results;
          }
        } catch (err) {
          console.warn("getProjectsForCurrentUser fallback:", err);
        }
      }

      // Dynamically discover/verify organization ID from the admin's assigned project
      if (assignedProjects.length > 0) {
        for (const p of assignedProjects) {
          const rawP = p.project && typeof p.project === "object" ? p.project : p;
          const pOrg = rawP.organization_id ?? rawP.org_id ?? rawP.org ?? rawP.organization?.id;
          if (pOrg && !isNaN(Number(pOrg)) && Number(pOrg) > 0) {
            if (!currentOrgId) {
              currentOrgId = Number(pOrg);
            }
            break;
          }
        }
      }

      setActiveOrgId(currentOrgId);
      setActiveOrgName(currentOrgName);

      // Collect set of assigned project IDs for this logged-in admin
      const assignedIds = new Set(
        assignedProjects
          .map((p) => {
            const rawP = p.project && typeof p.project === "object" ? p.project : p;
            return String(rawP.id ?? rawP.project_id ?? p.project_id ?? p.id);
          })
          .filter(Boolean)
      );

      // 2. Fetch overview from geofence API filtered by org_id
      let overview = [];
      try {
        overview = await fetchGeoFenceOverview(currentOrgId);
        if (!Array.isArray(overview)) overview = [];
      } catch (err) {
        console.warn("Geofence overview API fallback:", err);
      }

      // 3. Only fetch organization-wide projects as fallback IF the user has NO specific assigned projects
      let orgProjects = [];
      if (assignedProjects.length === 0 && currentOrgId) {
        try {
          const res = await getProjectsByOrganization(currentOrgId);
          if (Array.isArray(res?.data)) {
            orgProjects = res.data;
          } else if (Array.isArray(res?.data?.projects)) {
            orgProjects = res.data.projects;
          }
        } catch (err) {
          console.warn("getProjectsByOrganization fallback:", err);
          try {
            const ownRes = await getProjectsByOrgOwnership(currentOrgId);
            if (Array.isArray(ownRes?.data)) {
              orgProjects = ownRes.data;
            } else if (Array.isArray(ownRes?.data?.results)) {
              orgProjects = ownRes.data.results;
            }
          } catch (e) {
            console.warn("getProjectsByOrgOwnership fallback:", e);
          }
        }
      }

      // Fallback: If both are empty, check Allprojects strictly filtered by currentOrgId
      if (orgProjects.length === 0 && assignedProjects.length === 0 && overview.length === 0) {
        try {
          const pRes = await Allprojects();
          const all = Array.isArray(pRes.data) ? pRes.data : [];
          if (currentOrgId) {
            orgProjects = all.filter((p) => {
              const pOrg = p.organization_id ?? p.org_id ?? p.org ?? p.organization?.id;
              return Number(pOrg) === Number(currentOrgId);
            });
          } else {
            orgProjects = all;
          }
        } catch {}
      }

      // 4. Merge data into combinedMap: ONLY include assigned projects if assignedProjects is present
      const combinedMap = new Map();

      const addProjectToMap = (item, isExplicitlyAssigned = false) => {
        if (!item) return;
        const rawProj = item.project && typeof item.project === "object" ? item.project : item;
        const pid = rawProj.id ?? rawProj.project_id ?? item.project_id ?? item.id;
        if (!pid) return;

        const pOrg =
          rawProj.organization_id ??
          rawProj.org_id ??
          rawProj.org ??
          rawProj.organization?.id ??
          item.organization_id ??
          item.org_id ??
          item.org;

        // Skip other organizations for unassigned projects, but ALWAYS include explicitly assigned projects
        if (!isExplicitlyAssigned && currentOrgId && pOrg != null && Number(pOrg) !== Number(currentOrgId)) {
          return;
        }

        const idKey = String(pid);
        const existing = combinedMap.get(idKey) || {};
        const pName =
          rawProj.name ||
          rawProj.project_name ||
          rawProj.title ||
          item.project_name ||
          item.name ||
          existing.name ||
          `Project ${pid}`;

        // Retrieve local geofence cache if present
        let cached = null;
        try {
          const c = localStorage.getItem(`SITE_GEOFENCE_${pid}`);
          if (c) cached = JSON.parse(c);
        } catch {}

        const lat =
          rawProj.latitude ??
          item.latitude ??
          cached?.latitude ??
          existing.latitude ??
          null;
        const lon =
          rawProj.longitude ??
          item.longitude ??
          cached?.longitude ??
          existing.longitude ??
          null;
        const rad =
          rawProj.geofence_radius ??
          item.geofence_radius ??
          cached?.geofence_radius ??
          existing.geofence_radius ??
          100;
        const en =
          rawProj.geofence_enabled !== undefined
            ? rawProj.geofence_enabled
            : item.geofence_enabled !== undefined
            ? item.geofence_enabled
            : cached?.geofence_enabled !== undefined
            ? cached.geofence_enabled
            : existing.geofence_enabled !== false;
        const updated =
          rawProj.updated_at ??
          item.updated_at ??
          cached?.updated_at ??
          existing.updated_at ??
          null;

        const isLocConfigured = Boolean(
          en !== false &&
          lat !== null &&
          lat !== undefined &&
          lat !== "" &&
          lon !== null &&
          lon !== undefined &&
          lon !== ""
        );

        combinedMap.set(idKey, {
          ...existing,
          ...cached,
          ...item,
          ...rawProj,
          id: Number(pid) || pid,
          name: pName,
          organization_id: pOrg ?? currentOrgId,
          latitude: en === false ? null : lat,
          longitude: en === false ? null : lon,
          geofence_radius: rad,
          geofence_enabled: en !== false,
          updated_at: updated,
          is_geofence_configured: isLocConfigured,
          status: en === false ? "GEOFENCE_DISABLED" : isLocConfigured ? "GEOFENCE_ENABLED" : "LOCATION_NOT_CONFIGURED",
          is_assigned_to_user: isExplicitlyAssigned || existing.is_assigned_to_user || false,
        });
      };

      // 1. Add ONLY projects explicitly assigned to this logged-in admin if any exist
      if (assignedProjects.length > 0) {
        assignedProjects.forEach((p) => addProjectToMap(p, true));
      } else {
        // Fallback for super admin or general organization access
        orgProjects.forEach((p) => addProjectToMap(p, false));
      }

      // 2. Overlay geofence overview data strictly onto assigned projects
      overview.forEach((o) => {
        if (!o) return;
        const pid = String(o.id ?? o.project_id ?? o.project?.id);
        if (assignedProjects.length === 0 || assignedIds.has(pid)) {
          addProjectToMap(o, assignedIds.has(pid));
        }
      });

      // Purely dynamic list of real projects belonging to this logged-in admin's organization
      const list = Array.from(combinedMap.values());

      // Sort alphabetically by name
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      setProjects(list);
      setOverviewList(list);

      // Select configured site or first project if current selection is invalid or empty
      if (list.length > 0) {
        setSelectedSiteId((prev) => {
          const matched =
            list.find((item) => String(item.id) === String(prev)) ||
            list.find((item) => item.is_geofence_configured) ||
            list[0];
          const isLocConfigured = Boolean(
            matched?.latitude !== null &&
            matched?.latitude !== undefined &&
            matched?.latitude !== "" &&
            matched?.longitude !== null &&
            matched?.longitude !== undefined &&
            matched?.longitude !== ""
          );
          setSelectedSite(matched);
          setLatitude(matched?.latitude !== null && matched?.latitude !== undefined ? String(matched.latitude) : "");
          setLongitude(matched?.longitude !== null && matched?.longitude !== undefined ? String(matched.longitude) : "");
          setRadius(matched?.geofence_radius || 100);
          setEnabled(isLocConfigured && matched?.geofence_enabled !== false);
          return String(matched?.id);
        });
      } else {
        setSelectedSiteId("");
        setSelectedSite(null);
        setLatitude("");
        setLongitude("");
      }
    } catch (err) {
      console.error("Failed to load sites:", err);
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  useEffect(() => {
    loadProjectsAndOverview();
  }, [loadProjectsAndOverview]);

  // Load details when selectedSiteId changes
  const loadSelectedSiteDetails = useCallback(async (siteId) => {
    if (!siteId) return;

    setLoadingSite(true);
    setFormError("");
    setFormSuccess("");
    setCapturedLocation(null);
    setGpsAccuracy(null);

    try {
      const match = projects.find((p) => String(p.id) === String(siteId));
      let data = match || {};

      // Check local cache
      try {
        const cached = localStorage.getItem(`SITE_GEOFENCE_${siteId}`);
        if (cached) {
          data = { ...data, ...JSON.parse(cached) };
        }
      } catch {}

      // Try fetching latest from backend if numeric ID
      if (!isNaN(Number(siteId))) {
        try {
          const res = await fetchSiteGeoFence(siteId);
          if (res) data = { ...data, ...res };
        } catch (err) {
          console.warn("Could not fetch remote geofence:", err);
        }
      }

      const isSiteEnabled = data?.geofence_enabled !== false;
      const hasCoords = Boolean(
        isSiteEnabled &&
        data?.latitude !== null &&
        data?.latitude !== undefined &&
        data?.latitude !== "" &&
        data?.longitude !== null &&
        data?.longitude !== undefined &&
        data?.longitude !== ""
      );

      setSelectedSite({
        ...data,
        geofence_enabled: isSiteEnabled,
        latitude: isSiteEnabled ? data?.latitude : null,
        longitude: isSiteEnabled ? data?.longitude : null,
        is_geofence_configured: isSiteEnabled && hasCoords,
        status: isSiteEnabled ? (hasCoords ? "GEOFENCE_ENABLED" : "LOCATION_NOT_CONFIGURED") : "GEOFENCE_DISABLED",
      });
      setLatitude(isSiteEnabled && data?.latitude !== null && data?.latitude !== undefined ? String(data.latitude) : "");
      setLongitude(isSiteEnabled && data?.longitude !== null && data?.longitude !== undefined ? String(data.longitude) : "");
      setRadius(data?.geofence_radius || 100);
      setEnabled(isSiteEnabled);
      setHasUnsavedChanges(false);
    } catch (err) {
      setFormError("Failed to load site configuration details.");
    } finally {
      setLoadingSite(false);
    }
  }, [projects]);

  useEffect(() => {
    if (selectedSiteId) {
      loadSelectedSiteDetails(selectedSiteId);
    }
  }, [selectedSiteId, loadSelectedSiteDetails]);

  // Handle "Use My Current Location"
  const handleUseCurrentLocation = async () => {
    setIsCapturingGPS(true);
    setFormError("");
    setFormSuccess("");

    try {
      const coords = await getCurrentGPSLocation();
      const latStr = coords.latitude.toFixed(8);
      const lonStr = coords.longitude.toFixed(8);

      // Populate input fields
      setLatitude(latStr);
      setLongitude(lonStr);
      setGpsAccuracy(coords.accuracy);
      setCapturedLocation(coords);
      setHasUnsavedChanges(true);

      setFormSuccess(
        `Captured current GPS: ±${Math.round(coords.accuracy)}m accuracy. Click "Save Location" to apply.`
      );
      showToast(
        `Location captured (±${Math.round(coords.accuracy)}m accuracy). Remember to click Save Location.`,
        "info"
      );
    } catch (err) {
      console.error("GPS capture failed:", err);
      setFormError(err.userMessage || err.message || "Failed to capture current location.");
      showToast(err.userMessage || "Failed to get location", "error");
    } finally {
      setIsCapturingGPS(false);
    }
  };

  // Handle place selection from Search Bar
  const handleSelectSearchedLocation = useCallback((place) => {
    if (!place) return;
    const latStr = Number(place.latitude).toFixed(8);
    const lonStr = Number(place.longitude).toFixed(8);

    setLatitude(latStr);
    setLongitude(lonStr);
    setCapturedLocation(null);
    setGpsAccuracy(null);
    setHasUnsavedChanges(true);
    setFormError("");

    const nameToDisplay = place.name || place.displayName;
    setFormSuccess(
      `Selected "${nameToDisplay}". Latitude & Longitude updated! Click "Save Location" to apply.`
    );
    showToast("Coordinates set from search! Remember to click Save Location.", "info");
  }, []);

  // Handle direct map click coordinate adjustment
  const handleMapLocationSelect = useCallback((coords) => {
    if (!coords) return;
    const latStr = Number(coords.latitude).toFixed(8);
    const lonStr = Number(coords.longitude).toFixed(8);

    setLatitude(latStr);
    setLongitude(lonStr);
    setCapturedLocation(null);
    setGpsAccuracy(null);
    setHasUnsavedChanges(true);
    setFormError("");

    setFormSuccess(`Pin moved to [${latStr}, ${lonStr}]. Click "Save Location" to apply.`);
    showToast("Coordinates updated from map! Remember to click Save Location.", "info");
  }, []);

  // Handle "Save Location"
  const handleSaveLocation = async (e) => {
    if (e) e.preventDefault();
    setFormError("");
    setFormSuccess("");

    // Validate inputs
    const latNum = Number(latitude);
    const lonNum = Number(longitude);
    const radNum = Number(radius);

    if (enabled) {
      if (latitude === "" || longitude === "") {
        setFormError("Latitude and Longitude cannot be empty when Geo-Fencing is enabled.");
        return;
      }

      if (isNaN(latNum) || latNum < -90 || latNum > 90) {
        setFormError("Please enter a valid Latitude between -90 and 90 degrees.");
        return;
      }

      if (isNaN(lonNum) || lonNum < -180 || lonNum > 180) {
        setFormError("Please enter a valid Longitude between -180 and 180 degrees.");
        return;
      }
    }

    if (isNaN(radNum) || radNum <= 0) {
      setFormError("Geofence Radius must be greater than 0 meters.");
      return;
    }

    if (radNum > 5000) {
      setFormError("Geofence Radius cannot exceed 5,000 meters.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        latitude: enabled ? latNum : null,
        longitude: enabled ? lonNum : null,
        geofence_radius: radNum,
        geofence_enabled: enabled,
      };

      if (!isNaN(Number(selectedSiteId))) {
        await saveSiteGeoFence(selectedSiteId, payload);
      }

      if (!enabled) {
        setLatitude("");
        setLongitude("");
        setCapturedLocation(null);
        setGpsAccuracy(null);
      }

      setHasUnsavedChanges(false);
      setFormSuccess(
        enabled
          ? `Geo-fence for "${selectedSite?.name}" saved successfully!`
          : `Geo-fence disabled for "${selectedSite?.name}". Saved successfully!`
      );
      showToast(
        enabled
          ? `Location saved for ${selectedSite?.name}`
          : `Geo-fencing disabled for ${selectedSite?.name}`,
        "success"
      );

      // Refresh overview and site data
      await loadProjectsAndOverview();
    } catch (err) {
      console.error("Failed to save geofence:", err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to save location configuration. Ensure you have Admin authorization.";
      setFormError(detail);
      showToast(detail, "error");
    } finally {
      setSaving(false);
    }
  };

  // Open Edit Modal for a given site from overview table
  const handleOpenEditModal = (site) => {
    setEditingSite(site);
    setIsEditModalOpen(true);
    setSelectedSiteId(String(site.id));
  };

  // Callback when geofence is successfully saved from the modal
  const handleModalSaveSuccess = async (updatedSite) => {
    setLatitude(String(updatedSite.latitude));
    setLongitude(String(updatedSite.longitude));
    setRadius(updatedSite.geofence_radius || 100);
    setEnabled(updatedSite.geofence_enabled !== false);
    setSelectedSite(updatedSite);
    setHasUnsavedChanges(false);

    // Refresh projects list and overview data
    await loadProjectsAndOverview();
  };

  const isConfigured =
    enabled &&
    latitude !== "" &&
    longitude !== "" &&
    !isNaN(Number(latitude)) &&
    !isNaN(Number(longitude));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 space-y-5 sm:space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 rounded-xl px-4 py-3 sm:px-5 sm:py-3.5 text-white shadow-md flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="p-1.5 bg-white/20 backdrop-blur rounded-lg">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight">
              Site Geo-Fencing
            </h1>
            {activeOrgId && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-white/20 text-white border border-white/30 backdrop-blur">
                <Shield className="w-3 h-3" />
                {activeOrgName ? `${activeOrgName} (Org #${activeOrgId})` : `Org #${activeOrgId}`}
              </span>
            )}
          </div>
          <p className="text-orange-100/90 text-[11px] mt-0.5 max-w-2xl leading-normal">
            Configure independent geographic fences for each physical construction site in this organization.
            Physical presence within the configured radius is verified before site-restricted actions.
          </p>
        </div>

        <button
          type="button"
          onClick={loadProjectsAndOverview}
          disabled={loadingOverview}
          className="self-start md:self-auto px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur border border-white/30 text-white font-semibold text-xs flex items-center gap-1.5 transition shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loadingOverview ? "animate-spin" : ""}`} />
          <span>Refresh Sites</span>
        </button>
      </div>

      {/* Main Grid: Form on Left, Map Preview on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Configuration Form */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-6">
          <div className="border-b border-gray-100 pb-4">
            <h2 className="text-lg font-bold text-gray-900">Site Configuration</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Select a site to view and update its location parameters.
            </p>
          </div>

          {/* Site Selector Dropdown */}
          <div className="space-y-1.5">
            <label htmlFor="site-geofence-select" className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
              Select Site
            </label>
            <div className="relative">
              <select
                id="site-geofence-select"
                value={selectedSiteId}
                onChange={(e) => setSelectedSiteId(e.target.value)}
                disabled={loadingSite || saving || projects.length === 0}
                className="w-full appearance-none px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-gray-900 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition shadow-sm"
              >
                {projects.length === 0 ? (
                  <option value="">No projects found for active organization</option>
                ) : (
                  <optgroup label={activeOrgName ? `${activeOrgName} Projects` : activeOrgId ? `Organization #${activeOrgId} Projects` : "Assigned Projects"}>
                    {projects.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name} {site.geofence_enabled === false ? "⊘ (Disabled)" : (site.is_geofence_configured ? "✓ (Configured)" : "○ (Not Configured)")}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3.5 top-3.5 pointer-events-none" />
            </div>
          </div>

          {/* Location Configuration Inputs */}
          <div className="space-y-4 pt-2">
            {/* Latitude */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="text"
                disabled={!enabled}
                placeholder={!enabled ? "Geo-fencing disabled" : "e.g. 19.185000"}
                value={enabled ? latitude : ""}
                onChange={(e) => {
                  setLatitude(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className={`w-full px-4 py-2 rounded-xl border font-mono text-sm transition shadow-sm ${
                  !enabled
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                }`}
              />
            </div>

            {/* Longitude */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="text"
                disabled={!enabled}
                placeholder={!enabled ? "Geo-fencing disabled" : "e.g. 72.845000"}
                value={enabled ? longitude : ""}
                onChange={(e) => {
                  setLongitude(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className={`w-full px-4 py-2 rounded-xl border font-mono text-sm transition shadow-sm ${
                  !enabled
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                }`}
              />
            </div>

            {/* Geofence Radius */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs font-semibold text-gray-700">
                  Geofence Radius (meters)
                </label>
                <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md border border-orange-200">
                  Default: 100m
                </span>
              </div>
              <div className="relative">
                <input
                  type="number"
                  min="10"
                  max="5000"
                  step="5"
                  value={radius}
                  onChange={(e) => {
                    setRadius(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-4 py-2 rounded-xl border border-gray-300 text-sm text-gray-900 font-medium focus:ring-2 focus:ring-orange-500 focus:outline-none transition shadow-sm"
                />
                <span className="absolute right-4 top-2 text-sm text-gray-400 font-medium">
                  meters
                </span>
              </div>
            </div>

            {/* Status Toggle */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Status
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEnabled(true);
                    setHasUnsavedChanges(true);
                  }}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold border transition ${
                    enabled
                      ? "bg-emerald-500 border-emerald-600 text-white shadow-sm"
                      : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Enabled
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEnabled(false);
                    setLatitude("");
                    setLongitude("");
                    setCapturedLocation(null);
                    setGpsAccuracy(null);
                    setHasUnsavedChanges(true);
                    showToast("Geo-fencing disabled for this site. Click 'Save Location' to apply.", "info");
                  }}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold border transition ${
                    !enabled
                      ? "bg-rose-500 border-rose-600 text-white shadow-sm"
                      : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Disabled
                </button>
              </div>
            </div>

            {/* Prominent Banner when Geo-Fencing is disabled */}
            {!enabled && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>Geo-Fencing is currently <b>Disabled</b>. Coordinates are cleared and site boundary validation is completely bypassed. Click <b>"Save Location"</b> to apply.</span>
              </div>
            )}

            {/* GPS Accuracy Indicator */}
            {gpsAccuracy !== null && (
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 text-xs flex items-center gap-2">
                <Navigation className="w-4 h-4 text-blue-600 shrink-0" />
                <span>
                  Current GPS Accuracy: <b>±{Math.round(gpsAccuracy)} meters</b>
                </span>
              </div>
            )}

            {/* Unsaved Changes Banner */}
            {hasUnsavedChanges && (
              <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-center gap-2 font-medium">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Unsaved changes. Click <b>"Save Location"</b> to apply.</span>
              </div>
            )}

            {/* Feedback Alerts */}
            {formError && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            {formSuccess && (
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <span>{formSuccess}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-3 space-y-2.5">
              <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={isCapturingGPS || saving}
                className="w-full py-2.5 px-4 rounded-xl border border-orange-500 text-orange-600 hover:bg-orange-50 active:scale-98 font-bold text-sm flex items-center justify-center gap-2 shadow-sm transition disabled:opacity-60"
              >
                <Navigation className={`w-4 h-4 ${isCapturingGPS ? "animate-spin" : ""}`} />
                <span>{isCapturingGPS ? "Capturing Device Location..." : "Use My Current Location"}</span>
              </button>

              <button
                type="button"
                onClick={handleSaveLocation}
                disabled={saving || isCapturingGPS}
                className="w-full py-2.5 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 active:scale-98 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md transition disabled:opacity-60"
              >
                <Save className={`w-4 h-4 ${saving ? "animate-spin" : ""}`} />
                <span>{saving ? "Saving Location..." : "Save Location"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Interactive Map Preview */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                  <Layers className="w-4 h-4 text-orange-600" />
                  <span>Map Preview</span>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Visual verification of site center marker and 100m geofence radius.
                </p>
              </div>

              {!enabled ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-300 self-start sm:self-auto">
                  <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                  Disabled
                </span>
              ) : isConfigured ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300 self-start sm:self-auto">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  Configured
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 self-start sm:self-auto">
                  Location Not Configured
                </span>
              )}
            </div>

            {/* Place Search Bar on top of Map Preview */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-gray-700">
                  Search Place to Add Coordinates
                </label>
                <span className="text-[11px] text-gray-400">
                  Type place or click anywhere on map
                </span>
              </div>
              <MapLocationSearchBar
                onSelectLocation={handleSelectSearchedLocation}
                placeholder="Search landmark, street, building, or city (e.g. Chincholi Bunder Road, Malad)..."
              />
            </div>

            <MapPreview
              latitude={enabled ? latitude : null}
              longitude={enabled ? longitude : null}
              radius={radius}
              userLocation={enabled ? capturedLocation : null}
              siteName={selectedSite?.name || "Selected Site"}
              height={400}
              onLocationSelect={enabled ? handleMapLocationSelect : null}
              disabled={!enabled}
            />
          </div>
        </div>
      </div>

      {/* Admin Configuration Table: Organization Sites Overview */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden space-y-4 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Sites Geofence Overview</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Current configuration status for all {activeOrgName ? `${activeOrgName} ` : ""}project sites.
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-left text-xs">
            <thead className="bg-gray-50 text-gray-600 font-bold uppercase tracking-wider">
              <tr>
                <th className="py-3.5 px-4">Site Name</th>
                <th className="py-3.5 px-4">Location</th>
                <th className="py-3.5 px-4 text-right">Radius</th>
                <th className="py-3.5 px-4 text-center">Status</th>
                <th className="py-3.5 px-4">Last Updated</th>
                <th className="py-3.5 px-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium text-gray-800">
              {overviewList.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500 font-medium">
                    No project sites found for {activeOrgName || (activeOrgId ? `organization #${activeOrgId}` : "your organization")}.
                  </td>
                </tr>
              ) : (
                overviewList.map((site) => {
                  const isSiteEnabled = site.geofence_enabled !== false;
                  const isConfig = Boolean(
                    isSiteEnabled &&
                    site.is_geofence_configured &&
                    site.latitude !== null &&
                    site.latitude !== undefined &&
                    site.latitude !== "" &&
                    site.longitude !== null &&
                    site.longitude !== undefined &&
                    site.longitude !== ""
                  );
                  const isSelected = String(site.id) === String(selectedSiteId);

                  return (
                    <tr
                      key={site.id}
                      className={`hover:bg-orange-50/50 transition cursor-pointer ${
                      isSelected ? "bg-orange-50 font-semibold" : ""
                    }`}
                    onClick={() => setSelectedSiteId(String(site.id))}
                  >
                    <td className="py-3 px-4 font-bold text-gray-900 flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
                      <span>{site.name}</span>
                    </td>
                    <td className="py-3 px-4">
                      {!isSiteEnabled ? (
                        <span className="text-rose-700 bg-rose-50 px-2 py-0.5 rounded text-[11px] border border-rose-200 font-semibold">
                          Disabled
                        </span>
                      ) : isConfig ? (
                        <div className="font-mono text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded inline-block border border-emerald-200">
                          {Number(site.latitude).toFixed(6)}, {Number(site.longitude).toFixed(6)}
                        </div>
                      ) : (
                        <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded text-[11px] border border-amber-200 font-semibold">
                          Location Not Configured
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-mono">
                      {site.geofence_radius || 100}m
                    </td>
                    <td className="py-3 px-4 text-center">
                      {!isSiteEnabled ? (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-300">
                          Disabled
                        </span>
                      ) : isConfig ? (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                          Configured
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                          Not Configured
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-500 font-mono text-[11px]">
                      {site.updated_at
                        ? new Date(site.updated_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditModal(site);
                        }}
                        className="text-xs px-2.5 py-1 rounded-lg border border-orange-300 text-orange-600 hover:bg-orange-100 transition font-semibold"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Site Geofence Modal */}
      {isEditModalOpen && editingSite && (
        <EditSiteGeoFenceModal
          isOpen={isEditModalOpen}
          site={editingSite}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingSite(null);
          }}
          onSaveSuccess={handleModalSaveSuccess}
        />
      )}
    </div>
  );
}
